"use client";

import type { PostHog } from "posthog-js";

import { isIgnoredBrowserException } from "@/lib/posthog-exception-filter";

let initPromise: Promise<PostHog> | null = null;
/** Last user id passed to identify — skip redundant identify calls. */
let lastIdentifiedId: string | null = null;

function schedulePosthogLoad(): Promise<PostHog> {
	if (initPromise) return initPromise;
	initPromise = new Promise((resolve, reject) => {
		const start = () => {
			void import("posthog-js")
				.then((m) => {
					const posthog = m.default;
					if (process.env.NODE_ENV !== "development" && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
						posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
							api_host: "/ingest",
							ui_host: "https://us.posthog.com",
							defaults: "2025-05-24",
							capture_exceptions: true,
							debug: false,
							// Drop opaque Script error. / wallet-injected globals — not app bugs.
							before_send: (event) => {
								if (event?.event === "$exception" && isIgnoredBrowserException(event)) {
									return null;
								}
								return event;
							},
						});
					}
					resolve(posthog);
				})
				.catch(reject);
		};
		if (typeof window !== "undefined" && "requestIdleCallback" in window) {
			window.requestIdleCallback(start, { timeout: 4000 });
		} else {
			setTimeout(start, 1);
		}
	});
	return initPromise;
}

/** Starts idle-scheduled PostHog chunk load (call once from instrumentation). */
export function prefetchPosthog(): void {
	if (typeof window === "undefined") return;
	void schedulePosthogLoad();
}

export function getPosthog(): Promise<PostHog> {
	return schedulePosthogLoad();
}

export async function capturePosthogEvent(
	event: string,
	properties?: Record<string, unknown> | null,
): Promise<void> {
	const ph = await getPosthog();
	if (process.env.NODE_ENV === "development") return;
	ph.capture(event, properties ?? undefined);
}

export async function identifyPosthog(
	distinctId: string,
	properties?: Record<string, unknown> | null,
): Promise<void> {
	const ph = await getPosthog();
	if (process.env.NODE_ENV === "development") return;
	ph.identify(distinctId, properties ?? undefined);
	lastIdentifiedId = distinctId;
}

/**
 * Idempotent identity sync for authenticated sessions.
 * Calling identify while the browser still has an anonymous distinct_id merges
 * pre-auth $pageview / checkout events into the authenticated person.
 * Pass `null` on logout to reset.
 */
export async function syncPosthogIdentity(
	userId: string | null,
	properties?: Record<string, unknown> | null,
): Promise<void> {
	if (typeof window === "undefined") return;
	if (process.env.NODE_ENV === "development") return;
	if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;

	if (!userId) {
		if (lastIdentifiedId) {
			await resetPosthog();
		}
		return;
	}

	if (lastIdentifiedId === userId) return;
	await identifyPosthog(userId, properties);
}

export async function resetPosthog(): Promise<void> {
	const ph = await getPosthog();
	if (process.env.NODE_ENV === "development") {
		lastIdentifiedId = null;
		return;
	}
	ph.reset();
	lastIdentifiedId = null;
}

export async function capturePosthogException(error: unknown): Promise<void> {
	const ph = await getPosthog();
	if (process.env.NODE_ENV === "development") return;
	ph.captureException(error);
}

/** Test/helper — current identified id (null if anonymous or reset). */
export function getLastPosthogIdentifiedId(): string | null {
	return lastIdentifiedId;
}

/** Test-only reset of module identity cache. */
export function __resetPosthogIdentityCacheForTests(): void {
	lastIdentifiedId = null;
}
